import React, { useState, useEffect } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';
import { strapiService } from '../../../utils/api';

const SATConnectionMonitor = () => {
  const [certificateStatus, setCertificateStatus] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [fielResponse, syncHistoryResponse] = await Promise.all([
          strapiService.dashboard.getFIELStatus(),
          strapiService.dashboard.getSATSyncHistory()
        ]);
        setCertificateStatus(fielResponse.data || []);
        setRecentEvents(syncHistoryResponse.data || []);
      } catch (error) {
        console.error('Error fetching SAT connection data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Variables para datos de conexión que no están disponibles
  const connectionStatus = 'unknown';
  const lastSync = new Date();
  const connectionMetrics = {
    uptime: 0,
    responseTime: 0,
    successRate: 0,
    totalRequests: 0
  };
  const connectionHistory = [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-success';
      case 'disconnected': return 'text-error';
      case 'warning': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'connected': return 'bg-success/10';
      case 'disconnected': return 'bg-error/10';
      case 'warning': return 'bg-warning/10';
      default: return 'bg-muted/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return 'CheckCircle';
      case 'disconnected': return 'XCircle';
      case 'warning': return 'AlertTriangle';
      default: return 'Circle';
    }
  };

  const getCertificateStatusColor = (status) => {
    switch (status) {
      case 'valid': return 'text-success';
      case 'warning': return 'text-warning';
      case 'expired': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getCertificateStatusBg = (status) => {
    switch (status) {
      case 'valid': return 'bg-success/10';
      case 'warning': return 'bg-warning/10';
      case 'expired': return 'bg-error/10';
      default: return 'bg-muted/10';
    }
  };

  const formatTimestamp = (timestamp) => {
    return timestamp?.toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.value}
              {entry?.dataKey === 'responseTime' && 'ms'}
              {entry?.dataKey === 'requests' && ' req'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Connection Status Header */}
      <div className={`p-4 rounded-lg border ${getStatusBg(connectionStatus)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon 
              name={getStatusIcon(connectionStatus)} 
              size={24} 
              className={getStatusColor(connectionStatus)} 
            />
            <div>
              <h2 className="text-lg font-semibold">Estado de Conexión SAT</h2>
              <p className="text-sm text-muted-foreground">
                Última sincronización: {formatTimestamp(lastSync)}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold ${getStatusColor(connectionStatus)}`}>
              {connectionStatus === 'connected' ? 'CONECTADO' : 'DESCONECTADO'}
            </p>
            <p className="text-sm text-muted-foreground">
              Tiempo de respuesta: {connectionMetrics?.responseTime}ms
            </p>
          </div>
        </div>
      </div>
      {/* Connection Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Disponibilidad</p>
              <p className="text-2xl font-bold text-success">{connectionMetrics?.uptime}%</p>
              <p className="text-xs text-muted-foreground">Últimas 24 horas</p>
            </div>
            <Icon name="Activity" size={24} className="text-success" />
          </div>
        </div>

        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Tiempo de Respuesta</p>
              <p className="text-2xl font-bold text-foreground">{connectionMetrics?.responseTime}ms</p>
              <p className="text-xs text-muted-foreground">Promedio actual</p>
            </div>
            <Icon name="Clock" size={24} className="text-primary" />
          </div>
        </div>

        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Tasa de Éxito</p>
              <p className="text-2xl font-bold text-success">{connectionMetrics?.successRate}%</p>
              <p className="text-xs text-muted-foreground">Últimas 1000 peticiones</p>
            </div>
            <Icon name="CheckCircle" size={24} className="text-success" />
          </div>
        </div>

        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Peticiones Totales</p>
              <p className="text-2xl font-bold text-foreground">{connectionMetrics?.totalRequests?.toLocaleString('es-MX')}</p>
              <p className="text-xs text-muted-foreground">Hoy</p>
            </div>
            <Icon name="BarChart3" size={24} className="text-primary" />
          </div>
        </div>
      </div>
      {/* Connection History Chart */}
      <div className="bg-card rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Historial de Conexión (24 horas)</h3>
        <div className="w-full h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={connectionHistory} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="time" stroke="var(--color-muted-foreground)" fontSize={12} />
              <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="responseTime"
                stroke="var(--color-primary)"
                fill="var(--color-primary)"
                fillOpacity={0.6}
                name="Tiempo de Respuesta"
              />
              <Line
                type="monotone"
                dataKey="requests"
                stroke="var(--color-secondary)"
                strokeWidth={2}
                name="Peticiones"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Certificate Status and Recent Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Certificate Status */}
        <div className="bg-card rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Estado de Certificados FIEL</h3>
          <div className="space-y-3">
            {certificateStatus?.map((cert, index) => (
              <div 
                key={index} 
                className={`flex items-center justify-between p-3 rounded-lg border ${getCertificateStatusBg(cert?.status)}`}
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={cert?.status === 'valid' ? 'Shield' : cert?.status === 'warning' ? 'AlertTriangle' : 'ShieldX'} 
                    size={20} 
                    className={getCertificateStatusColor(cert?.status)} 
                  />
                  <div>
                    <p className="font-medium text-foreground">{cert?.company}</p>
                    <p className="text-sm text-muted-foreground">
                      Último uso: {cert?.lastUsed}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${getCertificateStatusColor(cert?.status)}`}>
                    {cert?.status === 'valid' && `${cert?.expiresIn} días`}
                    {cert?.status === 'warning' && `${cert?.expiresIn} días`}
                    {cert?.status === 'expired' && 'Expirado'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {cert?.status === 'expired' ? 'Renovar urgente' : 'hasta vencimiento'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Events */}
        <div className="bg-card rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Eventos Recientes</h3>
          <div className="space-y-3">
            {recentEvents?.map((event) => (
              <div key={event?.id} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
                <Icon 
                  name={
                    event?.type === 'success' ? 'CheckCircle' :
                    event?.type === 'warning' ? 'AlertTriangle' :
                    event?.type === 'error' ? 'XCircle' : 'Info'
                  } 
                  size={16} 
                  className={
                    event?.type === 'success' ? 'text-success' :
                    event?.type === 'warning' ? 'text-warning' :
                    event?.type === 'error' ? 'text-error' : 'text-primary'
                  }
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{event?.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">{event?.details}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatTimestamp(event?.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SATConnectionMonitor;